/**
 * Angel Ashes — Nameplate personalise (fonts, preview fit, step UI)
 * Split from booking-wizard.js for Shopify Theme Editor's 256 KB asset limit.
 * Loaded before booking-wizard.js from sections/booking-wizard.liquid.
 */
(function (global) {
  'use strict'

  function create(deps) {
    deps = deps || {}
    var escapeHtml = deps.escapeHtml
    var optionalSkipButtonHtml = deps.optionalSkipButtonHtml
    var firstAvailableVariant = deps.firstAvailableVariant
    var parseLineList = deps.parseLineList
    var stepCopyText = deps.stepCopyText
    var possessiveName = deps.possessiveName
    var isUsablePetName = deps.isUsablePetName
    var getMemorialCatalog = deps.getMemorialCatalog
    var getMemorialEntry = deps.getMemorialEntry
    var focusMemorialAddonProduct = deps.focusMemorialAddonProduct
    var memorialAddonSourceForProduct = deps.memorialAddonSourceForProduct
    var resolveMemorialAddonProductId = deps.resolveMemorialAddonProductId
    var memorialAddonHeadline = deps.memorialAddonHeadline
    var memorialAddonItemContext = deps.memorialAddonItemContext
    var renderMemorialPersonaliseIntroHtml = deps.renderMemorialPersonaliseIntroHtml
    var sanitizeEngravingLine = deps.sanitizeEngravingLine
    var ENGRAVING_LINE2_MAX = deps.ENGRAVING_LINE2_MAX
    var sizedShopifyImageUrl = deps.sizedShopifyImageUrl || function (url, width) {
      if (typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.sizedShopifyImageUrl) {
        return AngelAshesBookingMemorials.sizedShopifyImageUrl(url, width)
      }
      return url || ''
    }
    function nameplateArtboardUrl(url) {
      var px =
        (typeof AngelAshesBookingMemorials !== 'undefined' &&
          AngelAshesBookingMemorials.MEMORIAL_ARTBOARD_PX) ||
        800
      return sizedShopifyImageUrl(url, px)
    }

    /**
     * Hard caps on Write-your-own / first-line custom text so the 64×19 mm
     * preview stays on the plate at target ink size. Print Alice is the tighter
     * L1 face (average `n` leaves the plate at 15); L2 Source Serif `w` hits
     * 64 mm at 25 (worst-case). 26 still fits typical L2 (dates ~23) at body
     * print size. Keep in sync with lib/nameplate-fit.ts
     * NAMEPLATE_CUSTOM_LINE1_MAX / NAMEPLATE_CUSTOM_LINE2_MAX.
     */
    var NAMEPLATE_LINE1_MAX = 14
    var NAMEPLATE_LINE2_MAX = 26

    function sanitizeNameplateLine(value, max) {
      var s = String(value == null ? '' : value).replace(/[\r\n\u2028\u2029]/g, '')
      if (max && s.length > max) s = s.slice(0, max)
      return s
    }

    function clampNameplateInscription(nameplate) {
      if (!nameplate) return nameplate
      if (nameplate.text) {
        nameplate.text = sanitizeNameplateLine(nameplate.text, NAMEPLATE_LINE1_MAX) || null
      }
      if (nameplate.textLine2) {
        nameplate.textLine2 = sanitizeNameplateLine(nameplate.textLine2, NAMEPLATE_LINE2_MAX) || null
      }
      return nameplate
    }

  var NAMEPLATE_FONTS = [
    { id: 'alice', label: 'Print', cssFamily: "'Alice', Georgia, serif" },
    { id: 'lavanderia', label: 'Script', cssFamily: "'Lavanderia', cursive" },
  ]

  /** Second line is always the same body face — shown as helper text, not a picker. */
  var NAMEPLATE_BODY_FONT = 'source-serif'

  /** Default Second line dropdown copy when Theme Editor list is empty. */
  var NAMEPLATE_SECOND_LINE_SUGGESTIONS_DEFAULT = [
    'Forever loved',
    'Always in our hearts',
    'Gone but not forgotten',
    'Until we meet again',
    'Rest in peace',
    'Loved beyond words',
    'Forever in our hearts',
  ]
  /**
   * Hand-engraving L2 presets only — short sentiments (≤ ENGRAVING_LINE2_MAX).
   * Not shared with nameplate (sublimation can use longer TE / default phrases).
   * Current set fits Forever loved / Rest in peace / Forever missed at L2 ≤ 18.
   */
  var ENGRAVING_SECOND_LINE_SUGGESTIONS_DEFAULT = [
    'Forever loved',
    'Rest in peace',
    'Deeply loved',
    'Always loved',
    'Forever missed',
  ]
  var NAMEPLATE_SECOND_LINE_CUSTOM_VALUE = '__custom__'
  /** Engraving empty L2 option (first). Nameplate uses One/Two toggle instead. */
  var ENGRAVING_SECOND_LINE_NONE_OPTION_LABEL = 'No second line'

  function normalizeNameplateFont(value) {
    var v = String(value || '').trim().toLowerCase()
    for (var i = 0; i < NAMEPLATE_FONTS.length; i++) {
      var opt = NAMEPLATE_FONTS[i]
      if (opt.id === v) return opt.id
      if (String(opt.label || '').trim().toLowerCase() === v) return opt.id
    }
    return NAMEPLATE_FONTS[0].id
  }

  /** One/Two lines — unset/invalid defaults to One. */

  function normalizeNameplateLines(value) {
    return Number(value) === 2 ? 2 : 1
  }

  function nameplateFontDef(id) {
    var normalized = normalizeNameplateFont(id)
    return NAMEPLATE_FONTS.find(function (f) { return f.id === normalized }) || NAMEPLATE_FONTS[0]
  }

  function nameplateSecondLineSuggestions(config) {
    var fromConfig = parseLineList(config && config.nameplateSecondLineSuggestions)
    var list = fromConfig.length ? fromConfig : NAMEPLATE_SECOND_LINE_SUGGESTIONS_DEFAULT.slice()
    return list.filter(function (suggestion) {
      return String(suggestion || '').length <= NAMEPLATE_LINE2_MAX
    })
  }

  /**
   * Engraving Line 2 presets — short hand-engraving list only (not nameplate TE).
   * Filtered to ENGRAVING_LINE2_MAX so custom/draft limits stay consistent.
   */

  function engravingSecondLineSuggestions(/* config */) {
    return ENGRAVING_SECOND_LINE_SUGGESTIONS_DEFAULT.filter(function (suggestion) {
      return String(suggestion || '').length <= ENGRAVING_LINE2_MAX
    })
  }

  /** Resolve suggestion vs custom for two-line L2 (draft may only have textLine2). */

  function resolveNameplateSecondLineMode(nameplate, suggestions) {
    var mode = nameplate && nameplate.secondLineMode
    if (mode === 'custom' || mode === 'suggestion') return mode
    var text = nameplate ? String(nameplate.textLine2 || '').trim() : ''
    if (!text) return null
    var list = suggestions || []
    for (var i = 0; i < list.length; i++) {
      if (list[i] === text) return 'suggestion'
    }
    return 'custom'
  }

  /* --------------------------- photo print geometry --------------------------- */

  /** Theme Editor print size. Blocks saved before the setting existed emit null → 4x6. */

  function nameplateProductsForFlags(flags) {
    if (!flags) return []
    return [flags.nameplateProductGold, flags.nameplateProductSilver].filter(Boolean)
  }

  function findVariantById(product, variantId) {
    if (!product || !Array.isArray(product.variants) || variantId == null) return null
    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i]
      if (v && String(v.id) === String(variantId) && v.available !== false) return v
    }
    return null
  }

  function findNameplateVariantById(flags, variantId) {
    var products = nameplateProductsForFlags(flags)
    for (var i = 0; i < products.length; i++) {
      var found = findVariantById(products[i], variantId)
      if (found) return { variant: found, product: products[i] }
    }
    return null
  }

  /** Resolve the chosen Gold/Silver variant from live state or persisted choiceId. */

  function resolveNameplateAddonVariant(addon, flags) {
    if (!addon || String(addon.choiceId) === 'no') return null
    if (addon.variant && addon.variant.id != null && addon.variant.available !== false) {
      return addon.variant
    }
    var id = addon.variantId || addon.choiceId || (addon.variant && addon.variant.id)
    if (!id || !flags) return null
    var matched = findNameplateVariantById(flags, id)
    if (!matched || !matched.variant || matched.variant.available === false) return null
    return matched.variant
  }

  /**
   * Resolve the photo-print fee variant for summary / cart.
   * Prefer the live addon.variant; otherwise re-resolve from the Theme Editor
   * fee product (draft restore can leave variant null when variantId lookup fails).
   */

  function nameplateSummaryLabelForAddon(addon, flags) {
    if (!addon) return 'Nameplate'
    var options = buildNameplateColourOptions(flags)
    var opt = options.find(function (o) { return String(o.id) === String(addon.choiceId) })
    if (opt) {
      var title = String(opt.title || '').trim()
      if (/nameplate/i.test(title)) return title
      return title ? title + ' Nameplate' : 'Nameplate'
    }
    var short = String(addon.choiceLabel || '').trim()
    if (/nameplate/i.test(short)) return short
    return short ? short + ' Nameplate' : 'Nameplate'
  }

  /** Theme Editor colour label for nameplate tiles; blank falls back to Brushed defaults. */

  function nameplateColourLabelFromFlags(flags, colour) {
    if (colour === 'gold') {
      var gold = String((flags && flags.nameplateGoldLabel) || '').trim()
      return gold || 'Brushed Gold'
    }
    if (colour === 'silver') {
      var silver = String((flags && flags.nameplateSilverLabel) || '').trim()
      return silver || 'Brushed Silver'
    }
    return 'Nameplate'
  }

  function nameplateAddonTileImage(variant, product) {
    if (variant && variant.image) return variant.image
    if (product && product.image) return product.image
    return null
  }

  /**
   * Build nameplate colour choices — Gold + Silver are separate Shopify products
   * (not colour variants), one tile each, using each product's default variant.
   * Tile titles come from Theme Editor labels (not Shopify product titles).
   * Included + no fee products: preference-only Gold/Silver tiles (no charge).
   */

  function buildNameplateColourOptions(flags) {
    var options = []
    var gold = flags && flags.nameplateProductGold
    var silver = flags && flags.nameplateProductSilver
    ;[
      { product: gold, colour: 'gold' },
      { product: silver, colour: 'silver' },
    ].forEach(function (entry) {
      if (!entry.product) return
      var v = firstAvailableVariant(entry.product)
      if (!v || v.available === false) return
      options.push({
        id: String(v.id),
        title: nameplateColourLabelFromFlags(flags, entry.colour),
        variant: v,
        product: entry.product,
        colour: entry.colour,
        image: nameplateAddonTileImage(v, entry.product),
        preferenceOnly: false,
      })
    })
    if (options.length) return options
    // Free included plate: colour is a production preference + blank preview only.
    if (flags && flags.nameplateIncluded) {
      ;['gold', 'silver'].forEach(function (colour) {
        options.push({
          id: colour,
          title: nameplateColourLabelFromFlags(flags, colour),
          variant: null,
          product: null,
          colour: colour,
          image: null,
          preferenceOnly: true,
        })
      })
    }
    return options
  }

  /**
   * Resolve a committed nameplate colour only — never auto-select Gold/Silver.
   * Fresh open / decline (`choiceId` null or `'no'`) leave tiles empty; the
   * caller still seeds a sample preview (Gold blank visually, not on order).
   * Restores a draft/session colour when choiceId matches a live option.
   * Returns the selected option, or null when uncommitted / declined / unconfigured.
   */

  function ensureDefaultNameplateColour(nameplate, colourOptions, flags) {
    if (!nameplate || !colourOptions || !colourOptions.length) return null
    // Uncommitted or declined — leave tiles unselected; sample preview is separate.
    if (!nameplate.choiceId || String(nameplate.choiceId) === 'no') return null

    var selectedOpt =
      colourOptions.find(function (o) { return String(o.id) === String(nameplate.choiceId) }) ||
      (nameplate.colour
        ? colourOptions.find(function (o) { return o.colour === nameplate.colour })
        : null) ||
      null
    if (!selectedOpt) return null

    // Keep draft/restored choice; refresh label + variant from live catalogue.
    nameplate.choiceId = selectedOpt.id
    nameplate.colour = selectedOpt.colour || null
    nameplate.choiceLabel = nameplateSummaryLabelForAddon(
      { choiceId: selectedOpt.id, choiceLabel: selectedOpt.title },
      flags
    )
    nameplate.variant = selectedOpt.variant || null
    return selectedOpt
  }

  /** Blank plate image for the hero + live preview, best available first:
   *  1. Section TE blanks (`nameplateBlankGold` / `Silver` → config) — Files CDN
   *     via image_url when set in Theme Editor; liquid falls back to theme assets
   *  2. Product-block override (`nameplate_preview_gold` / `_silver` → flags)
   *  3. Selected Gold/Silver nameplate product image
   * CSS locks Gold/Silver to the same 64×19 box (object-fit cover) so padded TE
   * Files cannot shrink Silver vs Gold in the cream dock. */

  function nameplatePreviewImageForOption(flags, option, config) {
    if (!option) return null
    if (option.colour === 'gold') {
      if (config && config.nameplateBlankGold) return config.nameplateBlankGold
      if (flags && flags.nameplatePreviewImageGold) return flags.nameplatePreviewImageGold
    }
    if (option.colour === 'silver') {
      if (config && config.nameplateBlankSilver) return config.nameplateBlankSilver
      if (flags && flags.nameplatePreviewImageSilver) return flags.nameplatePreviewImageSilver
    }
    return option.image || null
  }

  /** Colour-chosen plate (no inscription UI yet) — same slot chrome as the live
   *  preview / ghost so the plate never jumps when personalise fields appear. */

  function renderNameplateHeroHtml(flags, selectedOpt, config) {
    if (!selectedOpt) return ''
    var img = nameplatePreviewImageForOption(flags, selectedOpt, config)
    if (!img) return ''
    var alt = selectedOpt.title ? selectedOpt.title + ' nameplate' : 'Nameplate'
    return (
      '<div class="baw-nameplate-preview baw-nameplate-preview--static" aria-live="polite">' +
      '<div class="baw-nameplate-preview__plate" data-nameplate-preview-plate>' +
      '<img class="baw-nameplate-preview__img" src="' + escapeHtml(nameplateArtboardUrl(img)) + '" alt="' + escapeHtml(alt) +
      '" width="640" height="190" loading="eager" decoding="async" />' +
      '</div>' +
      '<p class="baw-field-hint baw-field-hint--quiet baw-nameplate-preview__caption baw-nameplate-preview__caption--spacer" aria-hidden="true">&nbsp;</p>' +
      '</div>'
    )
  }

  /**
   * Customer-facing Goodbye Room option copy (tiles, rail, review, Collection Type notes).
   * Shopify Admin product/variant titles may still say “Short goodbye” / “Private time” —
   * checkout line titles follow Admin until renamed; GIDs / prices are unchanged.
   *
   * Old → display:
   *   Short goodbye · 5 minutes     → Short Goodbye · 5 min
   *   Private time · 30 minutes     → Private Goodbye · 30 min
   *   Private time · 60 minutes     → Extended Goodbye · 60 min
   */

    return {
      normalizeNameplateFont: normalizeNameplateFont,
      normalizeNameplateLines: normalizeNameplateLines,
      nameplateFontDef: nameplateFontDef,
      nameplateSecondLineSuggestions: nameplateSecondLineSuggestions,
      engravingSecondLineSuggestions: engravingSecondLineSuggestions,
      resolveNameplateSecondLineMode: resolveNameplateSecondLineMode,
      nameplateProductsForFlags: nameplateProductsForFlags,
      findNameplateVariantById: findNameplateVariantById,
      resolveNameplateAddonVariant: resolveNameplateAddonVariant,
      nameplateSummaryLabelForAddon: nameplateSummaryLabelForAddon,
      nameplateColourLabelFromFlags: nameplateColourLabelFromFlags,
      nameplateAddonTileImage: nameplateAddonTileImage,
      buildNameplateColourOptions: buildNameplateColourOptions,
      ensureDefaultNameplateColour: ensureDefaultNameplateColour,
      nameplatePreviewImageForOption: nameplatePreviewImageForOption,
      // LOCKED: INSTALL_SRC renderNameplatePreviewHtml runs under with(ctx) and
      // calls this — omitting it makes Continue from nameplate keepsakes a no-op.
      nameplateArtboardUrl: nameplateArtboardUrl,
      renderNameplateHeroHtml: renderNameplateHeroHtml,
      NAMEPLATE_FONTS: NAMEPLATE_FONTS,
      NAMEPLATE_BODY_FONT: NAMEPLATE_BODY_FONT,
      NAMEPLATE_SECOND_LINE_SUGGESTIONS_DEFAULT: NAMEPLATE_SECOND_LINE_SUGGESTIONS_DEFAULT,
      ENGRAVING_SECOND_LINE_SUGGESTIONS_DEFAULT: ENGRAVING_SECOND_LINE_SUGGESTIONS_DEFAULT,
      NAMEPLATE_SECOND_LINE_CUSTOM_VALUE: NAMEPLATE_SECOND_LINE_CUSTOM_VALUE,
      ENGRAVING_SECOND_LINE_NONE_OPTION_LABEL: ENGRAVING_SECOND_LINE_NONE_OPTION_LABEL,
      NAMEPLATE_LINE1_MAX: NAMEPLATE_LINE1_MAX,
      NAMEPLATE_LINE2_MAX: NAMEPLATE_LINE2_MAX,
      sanitizeNameplateLine: sanitizeNameplateLine,
      clampNameplateInscription: clampNameplateInscription
    }
  }

  var INSTALL_SRC = `    /**
     * Canonical per-product nameplate bucket (itemAddons[pid].nameplate).
     * Always re-focus — summary/ledger walks can leave entry.addons on another vessel.
     */
    function canonicalNameplateAddon(groupKey, productId) {
      var entry = getMemorialEntry(state, groupKey)
      if (!entry) return null
      var pid = resolveMemorialAddonProductId(state, config, groupKey, 'nameplate', productId)
      focusMemorialAddonProduct(entry, pid)
      if (pid != null && String(pid) !== '') {
        var buckets = entry.itemAddons && typeof entry.itemAddons === 'object' ? entry.itemAddons : null
        var key = String(pid)
        if (buckets) {
          if (!buckets[key] || typeof buckets[key] !== 'object') buckets[key] = {}
          entry.addons = buckets[key]
          entry._focusedAddonProductId = key
        }
      }
      if (!entry.addons) entry.addons = {}
      if (!entry.addons.nameplate) entry.addons.nameplate = {}
      return entry.addons.nameplate
    }

    /** True when the visible step is this product's nameplate personalise screen. */
    function isCurrentNameplateStep(groupKey, productId) {
      var cur = String(state.currentStepId || '')
      if (cur.indexOf(':addon:nameplate') === -1) return false
      var parsed = typeof parseMemorialAddonStepId === 'function' ? parseMemorialAddonStepId(cur) : null
      var curGroup = parsed && parsed.groupKey ? parsed.groupKey : cur.split(':')[1]
      if (String(curGroup || '') !== String(groupKey || '')) return false
      var curPid = resolveMemorialAddonProductId(
        state, config, groupKey, 'nameplate', parsed && parsed.productId
      )
      var wantPid = resolveMemorialAddonProductId(state, config, groupKey, 'nameplate', productId)
      if (wantPid == null || curPid == null) return true
      return String(curPid) === String(wantPid)
    }

    function openNameplatePreviewModal(groupKey, productId) {
      if (!groupKey) return
      var catalog = getMemorialCatalog(state, config)
      var pid = resolveMemorialAddonProductId(state, config, groupKey, 'nameplate', productId)
      // Pull live keystrokes / select value into canonical state before rendering —
      // otherwise the mag-glass can show the first-commit sample L2 after an edit.
      var nameplate = canonicalNameplateAddon(groupKey, pid)
      if (isCurrentNameplateStep(groupKey, pid)) {
        syncNameplateInscriptionFromDom(nameplate)
      }
      var source = memorialAddonSourceForProduct(
        state, config, groupKey, catalog, 'nameplate', pid
      )
      var flags = source && source.flags ? source.flags : null
      if (!nameplate || !nameplate.choiceId || String(nameplate.choiceId) === 'no') return
      var colourOptions = buildNameplateColourOptions(flags)
      var selectedOpt =
        colourOptions.find(function (o) {
          return String(o.id) === String(nameplate.choiceId)
        }) ||
        (nameplate.colour
          ? colourOptions.find(function (o) { return o.colour === nameplate.colour })
          : null) ||
        null
      var previewImage = nameplatePreviewImageForOption(flags, selectedOpt, config)
      var previewHtml = renderNameplatePreviewHtml(nameplate, previewImage)

      closePhotoPreviewModal()
      root.classList.add('has-photo-preview-modal')
      var wrap = document.createElement('div')
      wrap.className = 'baw-photo-preview-modal baw-photo-preview-modal--nameplate'
      wrap.setAttribute('data-baw-photo-preview-modal', '1')
      wrap.setAttribute('role', 'dialog')
      wrap.setAttribute('aria-modal', 'true')
      wrap.setAttribute('aria-label', 'Nameplate inscription preview')
      wrap.innerHTML =
        '<div class="baw-photo-preview-modal-backdrop" data-baw-photo-preview-close tabindex="-1"></div>' +
        '<div class="baw-photo-preview-modal-panel baw-photo-preview-modal-panel--nameplate">' +
        '<button type="button" class="baw-photo-preview-modal-close" data-baw-photo-preview-close aria-label="Close preview">' +
        '<span aria-hidden="true">&times;</span></button>' +
        '<div class="baw-photo-preview-modal-body">' + previewHtml + '</div></div>'
      root.appendChild(wrap)
      bindPreviewModalChrome(wrap, closePhotoPreviewModal)
      window.requestAnimationFrame(function () {
        fitNameplatePreview(wrap)
        ensureNameplateFontsThenFit(wrap)
      })
    }

    /** Hand-engraving text preview — name + optional second line (no fake plate). */

    function summaryNameplatePreviewButton(groupKey, productId) {
      if (!groupKey) return ''
      var productAttr = productId != null && String(productId) !== ''
        ? ' data-baw-nameplate-product="' + escapeHtml(String(productId)) + '"'
        : ''
      return summaryPreviewIconButton(
        ' data-baw-nameplate-preview="' + escapeHtml(groupKey) + '"' + productAttr,
        'View nameplate'
      )
    }

    function focusNameplateSecondLine() {
      var custom = els.step && els.step.querySelector('input[data-nameplate-line2]')
      var select = els.step && els.step.querySelector('select[data-nameplate-line2-select]')
      var target = custom || select
      if (!target) return
      // Prefer scrolling the nameplate band (not the page) so the custom L2
      // clears the cream dock after Write your own reflows the controls.
      try { target.focus({ preventScroll: true }) } catch (err) {
        try { target.focus() } catch (err2) { /* ignore */ }
      }
      syncFunnelChromePadding()
      var scroll = els.step.querySelector(
        '.baw-step-inner.is-nameplate-docked > .baw-nameplate-scroll'
      )
      if (!scroll) return
      window.requestAnimationFrame(function () {
        var tRect = target.getBoundingClientRect()
        var sRect = scroll.getBoundingClientRect()
        var pad = 16
        if (tRect.bottom > sRect.bottom - pad) {
          scroll.scrollTop += Math.ceil(tRect.bottom - (sRect.bottom - pad))
        } else if (tRect.top < sRect.top + pad) {
          scroll.scrollTop -= Math.ceil((sRect.top + pad) - tRect.top)
        }
      })
    }

    /** Sync L2 from the live select / custom input before Continue validates. */

    function syncNameplateSecondLineFromDom(nameplate) {
      if (!nameplate || !els.step) return
      var select = els.step.querySelector('select[data-nameplate-line2-select]')
      var custom = els.step.querySelector('input[data-nameplate-line2]')
      if (!select && !custom) return
      if (select) {
        var value = String(select.value || '')
        if (value === NAMEPLATE_SECOND_LINE_CUSTOM_VALUE) {
          nameplate.secondLineMode = 'custom'
          var rawCustom = custom ? sanitizeNameplateLine(custom.value, NAMEPLATE_LINE2_MAX) : ''
          if (custom && rawCustom !== custom.value) custom.value = rawCustom
          nameplate.textLine2 = rawCustom.trim() || null
          if (nameplate.textLine2) nameplate._line2Touched = true
          return
        }
        if (value) {
          nameplate.secondLineMode = 'suggestion'
          nameplate.textLine2 = sanitizeNameplateLine(value, NAMEPLATE_LINE2_MAX) || null
          if (nameplate.textLine2) nameplate._line2Touched = true
          return
        }
        nameplate.secondLineMode = null
        nameplate.textLine2 = null
        return
      }
      // Fallback: custom input only (should not happen in two-line picker UI).
      nameplate.secondLineMode = 'custom'
      var rawFallback = custom ? sanitizeNameplateLine(custom.value, NAMEPLATE_LINE2_MAX) : ''
      if (custom && rawFallback !== custom.value) custom.value = rawFallback
      nameplate.textLine2 = rawFallback.trim() || null
      if (nameplate.textLine2) nameplate._line2Touched = true
    }

    /**
     * Sync the full live inscription (L1 / L2 / lines / font) into canonical state.
     * Used by Continue and the summary mag-glass so neither reads a first-commit
     * sample snapshot while the step controls already show a later edit.
     */

    function syncNameplateInscriptionFromDom(nameplate) {
      if (!nameplate || !els.step) return
      var line1 = els.step.querySelector('input[data-nameplate-line1]')
      if (line1) {
        var raw1 = sanitizeNameplateLine(line1.value, NAMEPLATE_LINE1_MAX)
        if (raw1 !== line1.value) line1.value = raw1
        nameplate.text = raw1.trim() || null
      }
      var linesBtn = els.step.querySelector(
        'button.baw-nameplate-layout__btn[data-nameplate-lines].is-selected, ' +
        'button.baw-nameplate-layout__btn[data-nameplate-lines][aria-pressed="true"]'
      )
      if (linesBtn) {
        nameplate.lines = normalizeNameplateLines(linesBtn.getAttribute('data-nameplate-lines'))
        nameplate._linesTouched = true
      }
      var fontBtn = els.step.querySelector(
        'button.baw-nameplate-font[data-nameplate-font].is-selected, ' +
        'button.baw-nameplate-font[data-nameplate-font][aria-pressed="true"]'
      )
      if (fontBtn) {
        nameplate.font = normalizeNameplateFont(fontBtn.getAttribute('data-nameplate-font'))
        nameplate._fontTouched = true
      }
      syncNameplateSecondLineFromDom(nameplate)
      if (normalizeNameplateLines(nameplate.lines) === 1) {
        nameplate.textLine2 = null
        nameplate.secondLineMode = null
      }
      clampNameplateInscription(nameplate)
    }

      /** Continue guard — sync live inscription.
     *  Two lines → Second line is required once a colour is committed.
     *  Included + still uncommitted: commit the sample preview (incl. sample L2)
     *  so Continue does not silently omit a plate that comes with the memorial.
     *  Paid with no colour selected remains a valid skip. */

    function tryAdvanceMemorialNameplateStep(groupKey, productId) {
      var catalog = getMemorialCatalog(state, config)
      var pid = resolveMemorialAddonProductId(state, config, groupKey, 'nameplate', productId)
      var nameplate = canonicalNameplateAddon(groupKey, pid)
      if (!nameplate) return true
      // Prefer the live controls so Continue / cart / mag-glass see latest edits.
      syncNameplateInscriptionFromDom(nameplate)
      clampNameplateInscription(nameplate)
      if (
        String(nameplate.text || '').length > NAMEPLATE_LINE1_MAX ||
        String(nameplate.textLine2 || '').length > NAMEPLATE_LINE2_MAX
      ) {
        return false
      }
      var source = memorialAddonSourceForProduct(
        state, config, groupKey, catalog, 'nameplate', pid
      )
      var flags = source && source.flags ? source.flags : null
      if (flags && flags.nameplateIncluded) {
        var colourOptions = buildNameplateColourOptions(flags)
        if (!ensureDefaultNameplateColour(nameplate, colourOptions, flags)) {
          commitIncludedNameplateSampleDefaults(nameplate, colourOptions, flags)
        }
      }
      var hasColour = !!(nameplate.choiceId && String(nameplate.choiceId) !== 'no')
      if (
        hasColour &&
        normalizeNameplateLines(nameplate.lines) === 2 &&
        !String(nameplate.textLine2 || '').trim()
      ) {
        nameplate.missingLine2Error = true
        renderMemorialAddonNameplateStep(groupKey, pid)
        focusNameplateSecondLine()
        return false
      }
      nameplate.missingLine2Error = false
      return true
    }

    function renderMemorialAddonNameplateStep(groupKey, productId) {
      var catalog = getMemorialCatalog(state, config)
      var pid = resolveMemorialAddonProductId(state, config, groupKey, 'nameplate', productId)
      var nameplate = canonicalNameplateAddon(groupKey, pid)
      var source = memorialAddonSourceForProduct(
        state, config, groupKey, catalog, 'nameplate', pid
      )
      var flags = source && source.flags ? source.flags : null
      var nameplateIncluded = !!(flags && flags.nameplateIncluded)
      var colourOptions = buildNameplateColourOptions(flags)
      var dualMode = !!(flags && (flags.nameplateProductGold || flags.nameplateProductSilver))
      var hasConfiguredOptions = colourOptions.length > 0
      var askText = !!(flags && flags.needsNameplateText)

      // First screen: empty radios + sample preview until a colour is clicked.
      // Restore a draft/session colour when already committed; never auto-pick Gold
      // on open. Included skip / uncommitted Continue commits that sample instead
      // of omitting the plate. Paid skip still declines. Click selected colour
      // again → back to sample; skip stays under tiles when empty.
      var tiles = colourOptions.map(function (opt) {
        var priceLabel = ''
        if (opt.variant) {
          priceLabel = money(opt.variant.price)
        } else if (nameplateIncluded) {
          priceLabel = 'Included'
        }
        return {
          id: opt.id,
          title: opt.title,
          // Physical visible plate size — subtle meta on the colour cards.
          desc: '64 \\u00d7 19 mm',
          priceLabel: priceLabel,
        }
      })

      var selectedOpt = ensureDefaultNameplateColour(
        nameplate,
        colourOptions,
        flags
      )
      var itemContext = memorialAddonItemContext(
        catalog,
        groupKey,
        source,
        selectedOpt && selectedOpt.colour
      )
      // Prefetch Gold + Silver heroes for this product before paint / colour toggle.
      if (
        typeof AngelAshesBookingMemorials !== 'undefined' &&
        AngelAshesBookingMemorials.prefetchImageUrls
      ) {
        var warmPair = []
        var thumbPx = AngelAshesBookingMemorials.MEMORIAL_GRID_THUMB_PX || 360
        var sizeUrl = AngelAshesBookingMemorials.sizedShopifyImageUrl || function (u) { return u }
        ;[itemContext.personaliseImageGold, itemContext.personaliseImageSilver, itemContext.personaliseImage]
          .forEach(function (raw) {
            if (!raw) return
            var sized = sizeUrl(raw, thumbPx)
            if (sized && warmPair.indexOf(sized) === -1) warmPair.push(sized)
          })
        AngelAshesBookingMemorials.prefetchImageUrls(warmPair, { high: true })
      }
      var selectedId = selectedOpt ? selectedOpt.id : null
      var committed = !!selectedOpt
      // Sample preview (uncommitted / declined) uses Gold (or sole finish) blanks
      // visually — does not set choiceId / choiceLabel / variant.
      var previewOpt =
        selectedOpt ||
        colourOptions.find(function (o) { return o.colour === 'gold' }) ||
        colourOptions[0] ||
        null
      // Inscription editor only after a real colour commit (not sample / 'no').
      var showText = askText && committed
      var previewImage = nameplatePreviewImageForOption(flags, previewOpt, config)

      // Seed pet L1 + One line + Print without committing a colour or filling L2.
      // Sample L2 is display-only on the uncommitted plate (not written to textLine2).
      // Committed values (incl. continue-without-personalising defaults) stay in the
      // form fields so going back matches summary / mag-glass state.
      ensureNameplateInscriptionDefaults(nameplate)

      // Line-mode chrome nests under Gold/Silver in the personalise stack; fonts +
      // inscription fields stay in extraHtml below (not under the thumb).
      var layoutChromeHtml = showText
        ? renderNameplateLayoutHtml(nameplate)
        : ''
      var textHtml = showText ? renderNameplatePersonaliseHtml(nameplate) : ''

      // Live preview when committed; full-color sample + caption when uncommitted.
      // Omit only when no colour options are available (paid needs products; included
      // can use preference-only Gold/Silver). Docked under the scroll band — not the
      // personalise-row intro (that slot is the parent product thumb beside colours).
      var heroHtml
      if (askText && previewOpt) {
        heroHtml = renderNameplatePreviewHtml(nameplate, previewImage, {
          sample: !committed,
          included: nameplateIncluded,
        })
      } else if (previewOpt) {
        heroHtml = renderNameplateHeroHtml(flags, previewOpt, config)
      } else {
        heroHtml = ''
      }

      // Photo-window products (Ink Photo Set, Clay Photo Frame, Photo Tribute):
      // composite photoUrl / localPreviewUrl into the mockup cutout. Circular
      // Clay/urn thumbs stay personalise PNG only — never overlay a portrait.
      var productThumbHtml = ''
      if (flags && flags.needsPhoto && typeof renderPersonalisePhotoMockupHtml === 'function') {
        var liveEntry = getMemorialEntry(state, groupKey)
        var photoBucket =
          liveEntry && liveEntry.itemAddons && pid != null
            ? liveEntry.itemAddons[String(pid)]
            : null
        var photoAddon =
          (photoBucket && photoBucket.photo) ||
          (liveEntry && liveEntry.addons && liveEntry.addons.photo) ||
          null
        var mockupColour =
          (selectedOpt && selectedOpt.colour) ||
          (previewOpt && previewOpt.colour) ||
          null
        productThumbHtml = renderPersonalisePhotoMockupHtml(
          photoAddon,
          flags,
          'baw-personalise-product baw-personalise-product--mockup',
          mockupColour,
          itemContext && itemContext.label
        )
      }
      if (!productThumbHtml) {
        productThumbHtml = renderMemorialPersonaliseIntroHtml(itemContext)
      }

      var helperHtml
      if (!hasConfiguredOptions) {
        if (!isBookingThemeEditorPreview()) {
          helperHtml = ''
        } else if (dualMode) {
          // Gold/Silver are set on the block but neither has an available variant right
          // now (draft status, zero inventory, or not selling on this channel).
          helperHtml = '<p>Gold/Silver nameplate products are set on this block, but neither has an available variant. Check their status and inventory in Shopify Admin.</p>'
        } else if (nameplateIncluded) {
          helperHtml = '<p>Included nameplate is on — set Gold/Silver blank plate images on this block or under Booking wizard section settings so families can preview the plate.</p>'
        } else {
          helperHtml = '<p>Add Gold and Silver nameplate products on the Memorials — Product block for this item.</p>'
        }
      } else {
        // Colour + price are self-evident from the tiles below — no helper needed.
        helperHtml = ''
      }

      renderTileStep({
        headline: memorialAddonHeadline('nameplate', itemContext.label, config, {
          included: nameplateIncluded,
        }),
        introHtml: productThumbHtml,
        personaliseRow: !!productThumbHtml,
        personaliseStackExtra: layoutChromeHtml,
        helper: helperHtml,
        layout: 'inline',
        tiles: tiles,
        selectedId: selectedId,
        // Skip under colour tiles only when nothing is committed. Deselect colour
        // (click selected again) clears choiceId → skip returns; Continue stays Continue.
        // Preview stays in the step so layoutNameplateDock can pin it; inscription follows.
        extraHtml: heroHtml + textHtml,
        deselectable: true,
        skipLabel: nameplateIncluded
          ? 'Continue without personalising'
          : 'No thanks — continue without a nameplate',
        onSelect: function (id) {
          // Re-resolve canonical bucket — renderSummary/buildLedger may have left
          // entry.addons pointing at another multi-select urn's bucket.
          var live = canonicalNameplateAddon(groupKey, pid)
          // Toggle off the selected colour → decline (not in summary / cart).
          if (committed && String(id) === String(selectedId)) {
            clearNameplateSelection(live)
            renderMemorialAddonNameplateStep(groupKey, pid)
            renderSummary()
            return
          }
          var opt = colourOptions.find(function (x) { return String(x.id) === String(id) })
          if (!opt) return
          // Unselected → colour: drop marketing sample L2 so the editor shows
          // "Choose a second line…". Keep a value the family already chose.
          if (!committed && !live._line2Touched) {
            live.textLine2 = null
            live.secondLineMode = null
          }
          live.choiceId = id
          live.colour = opt.colour || null
          live.choiceLabel = nameplateSummaryLabelForAddon(
            { choiceId: id, choiceLabel: opt.title },
            flags
          )
          live.variant = opt.variant || null
          renderMemorialAddonNameplateStep(groupKey, pid)
          renderSummary()
        },
        onSkip: function () {
          var live = canonicalNameplateAddon(groupKey, pid)
          if (nameplateIncluded) {
            // Accept the sample preview as the included plate (not a decline).
            commitIncludedNameplateSampleDefaults(
              live,
              colourOptions,
              flags
            )
          } else {
            // Paid optional plate — true decline; leave nothing on the order.
            clearNameplateSelection(live)
          }
          renderSummary()
          goNext()
        },
        onContinue: function () {
          goNext()
        },
      })

      wireNameplatePersonalise(null, groupKey, pid)
      if (typeof layoutNameplateDock === 'function') layoutNameplateDock()
      if (typeof scheduleNameplateFit === 'function') scheduleNameplateFit()
      if (typeof wirePhotoPreviewDecodeIn === 'function') {
        wirePhotoPreviewDecodeIn(els.step)
        // Gold↔Silver swaps the frame PNG — re-measure after layout so the
        // right-pane inset stays aligned (match Gold behavior).
        if (typeof resizePhotoMockupsIn === 'function' && typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(function () {
            resizePhotoMockupsIn(els.step)
          })
        }
      }
      // Disabled Continue does not receive clicks — let the actions strip catch
      // them so missing Second line still surfaces inline error + focus.
      var nameplateActionsEl = els.footer && els.footer.querySelector('.baw-actions')
      if (nameplateActionsEl && !nameplateActionsEl.getAttribute('data-nameplate-validate-bound')) {
        nameplateActionsEl.setAttribute('data-nameplate-validate-bound', '1')
        nameplateActionsEl.addEventListener('click', function () {
          var btn = nameplateActionsEl.querySelector('[data-baw-continue]')
          if (btn && btn.disabled) {
            tryAdvanceMemorialNameplateStep(groupKey, pid)
          }
        })
      }
      // Shared readiness: skip OK with no colour; L2 optional in Two lines mode.
      if (typeof syncFunnelChromePadding === 'function') syncFunnelChromePadding()
      if (typeof setContinueEnabled === 'function') {
        setContinueEnabled(
          typeof canAdvanceFrom === 'function' ? canAdvanceFrom(state.currentStepId) : true
        )
      }
      if (typeof restoreA11yFocus === 'function') restoreA11yFocus()
    }

    /**
     * Pin the nameplate preview under a scrollable options band (all breakpoints)
     * so the plate stays visible above Continue / Booking Total while the family
     * scrolls and types. Same cream dock section as mobile — desktop no longer
     * keeps the preview under the H1 (avoids a large empty void above the tiles).
     */

    function layoutNameplateDock() {
      var inner = els.step && els.step.querySelector('.baw-step-inner')
      if (!inner) return
      var preview = inner.querySelector('.baw-nameplate-preview')
      if (!preview) {
        inner.classList.remove('baw-step-inner--nameplate', 'is-nameplate-docked')
        return
      }
      // Engraving reuses the cream dock but must NOT get nameplate densify /
      // colour-tile rules via \`.baw-step-inner--nameplate\`.
      if (!inner.classList.contains('baw-step-inner--engraving')) {
        inner.classList.add('baw-step-inner--nameplate')
      } else {
        inner.classList.remove('baw-step-inner--nameplate')
      }

      var scroll = inner.querySelector(':scope > .baw-nameplate-scroll')
      if (!scroll) {
        scroll = document.createElement('div')
        scroll.className = 'baw-nameplate-scroll'
        while (inner.firstChild) scroll.appendChild(inner.firstChild)
        inner.appendChild(scroll)
        preview = scroll.querySelector('.baw-nameplate-preview') || preview
      }

      if (preview.parentNode !== inner) inner.appendChild(preview)
      inner.classList.add('is-nameplate-docked')
      syncFunnelChromePadding()
    }

    /** Back to first-screen (sample preview, no editor, not on order).
     *  choiceId \`'no'\` (photo sentinel) marks an explicit decline so a later
     *  re-enter does not look like a fresh null — same empty-tile UX either way.
     *  Summary/cart only carry a plate while choiceLabel is set. */

    function clearNameplateSelection(nameplate) {
      if (!nameplate) return
      nameplate.choiceId = 'no'
      nameplate.choiceLabel = null
      nameplate.variant = null
      nameplate.variantId = null
      nameplate.colour = null
      nameplate.missingLine2Error = false
    }

    /**
     * Included nameplate — put the on-screen sample preview onto the order.
     * Same defaults the uncommitted plate already shows: Gold (or sole finish),
     * pet name on L1, sample L2 when Two lines, Print font. Returns the colour
     * option committed, or null when no finishes are configured.
     */

    function commitIncludedNameplateSampleDefaults(nameplate, colourOptions, flags) {
      if (!nameplate) return null
      ensureNameplateInscriptionDefaults(nameplate)
      var options = colourOptions || []
      var opt =
        options.find(function (o) { return o.colour === 'gold' }) ||
        options[0] ||
        null
      if (!opt) return null
      nameplate.choiceId = opt.id
      nameplate.colour = opt.colour || null
      nameplate.choiceLabel = nameplateSummaryLabelForAddon(
        { choiceId: opt.id, choiceLabel: opt.title },
        flags
      )
      nameplate.variant = opt.variant || null
      // Persist marketing sample L2 the family already saw (display-only until now).
      if (
        normalizeNameplateLines(nameplate.lines) === 2 &&
        !(String(nameplate.textLine2 || '').trim())
      ) {
        var sampleL2 = nameplateSampleLine2DisplayText()
        if (sampleL2) {
          nameplate.textLine2 = sampleL2
          nameplate.secondLineMode = resolveNameplateSecondLineMode(
            nameplate,
            nameplateSecondLineSuggestions(config)
          )
          nameplate._line2Touched = true
        }
      }
      nameplate.missingLine2Error = false
      return opt
    }

    /** Seed line 1 from the pet name when the family hasn't typed an inscription yet. */

    function preloadNameplateTextFromPet(nameplate) {
      if (!nameplate) return
      if ((nameplate.text || '').trim()) return
      var petName = state.pet && state.pet.name ? String(state.pet.name).trim() : ''
      if (petName) nameplate.text = sanitizeNameplateLine(petName, NAMEPLATE_LINE1_MAX)
    }

    /**
     * Marketing sample for the uncommitted plate only — never written to
     * textLine2 / the Second line control. Prefers “Always in our hearts”.
     */

    function nameplateSampleLine2DisplayText() {
      var suggestions = nameplateSecondLineSuggestions(config)
      if (!suggestions.length) return ''
      for (var i = 0; i < suggestions.length; i++) {
        if (/^always in our hearts$/i.test(String(suggestions[i] || '').trim())) {
          return suggestions[i]
        }
      }
      return suggestions[0]
    }

    /** Conversion open-state: One line + Print + pet name.
     *  Do not pre-fill textLine2 — sample L2 is display-only when Two lines is
     *  selected until colour commit, then the editor shows “Choose a second
     *  line…” until they pick a suggestion/custom.
     *  Apply One line unless the family explicitly picked One/Two lines this
     *  session (\`_linesTouched\`). After any lines click, never force One line —
     *  only normalize the committed value.
     *  Font is never forced back to Print — only normalized (unset → Print). */

    function ensureNameplateInscriptionDefaults(nameplate) {
      if (!nameplate) return
      if (nameplate._linesTouched) {
        nameplate.lines = normalizeNameplateLines(nameplate.lines)
      } else {
        nameplate.lines = 1
      }
      nameplate.font = normalizeNameplateFont(nameplate.font)
      preloadNameplateTextFromPet(nameplate)
      clampNameplateInscription(nameplate)
    }

    /** Pet name for font-tile samples; truncated so long names don't blow the button. */

    function nameplateFontSampleText() {
      var petName = state.pet && state.pet.name ? String(state.pet.name).trim() : ''
      if (!petName) return 'Name'
      if (petName.length > 10) return petName.slice(0, 9) + '\\u2026'
      return petName
    }

    /** One/Two line cards — nested under Gold/Silver in the personalise stack. */

    function renderNameplateLayoutHtml(nameplate) {
      nameplate = nameplate || {}
      // Prefer One line when unset — never paint Two lines as selected by accident
      // (\`=== 1 ? 1 : 2\` made null look like Two lines).
      var lines = normalizeNameplateLines(nameplate.lines)
      nameplate.lines = lines
      return (
        '<div class="baw-nameplate-controls">' +
        '<div class="baw-nameplate-control" role="group" aria-label="Lines">' +
        '<div class="baw-nameplate-layout">' +
        [{ id: 1, label: 'One line' }, { id: 2, label: 'Two lines' }].map(function (opt) {
          var selected = opt.id === lines
          return (
            '<button type="button" class="baw-nameplate-layout__btn' + (selected ? ' is-selected' : '') + '"' +
            ' data-nameplate-lines="' + opt.id + '" aria-pressed="' + (selected ? 'true' : 'false') + '"' +
            ' aria-label="' + escapeHtml(opt.label) + '">' +
            '<span class="baw-tile-check" aria-hidden="true"></span>' +
            '<span class="baw-nameplate-layout__label">' + escapeHtml(opt.label) + '</span>' +
            '</button>'
          )
        }).join('') +
        '</div></div></div>'
      )
    }

    /** Font + inscription fields once a plate colour is chosen. Line-mode chrome
     *  is rendered separately into the personalise stack (under Gold/Silver).
     *  The live preview they drive is docked at the bottom of the step.
     *  One template for One/Two lines: always “First line” + Print/Script (no
     *  font heading); Second line block is appended only when lines === 2. */

    function renderNameplateCharCounter(count, max, id) {
      return (
        '<p class="baw-field-hint baw-nameplate-counter" id="' + id + '" aria-live="polite">' +
        count + ' / ' + max +
        '</p>'
      )
    }

    function renderNameplatePersonaliseHtml(nameplate) {
      nameplate = nameplate || {}
      var lines = normalizeNameplateLines(nameplate.lines)
      nameplate.lines = lines
      clampNameplateInscription(nameplate)
      var font = normalizeNameplateFont(nameplate.font)
      var line1 = (nameplate.text || '').trim()
      var line2 = (nameplate.textLine2 || '').trim()
      var fontSample = nameplateFontSampleText()
      var suggestions = nameplateSecondLineSuggestions(config)
      var secondMode = resolveNameplateSecondLineMode(nameplate, suggestions)
      // Keep inferred mode on the entry so draft/summary stay consistent.
      nameplate.secondLineMode = secondMode

      // Identical DOM for Print + Script — check left, sample + label; selected
      // state is border/bg/check only (no weight/size change). Face on sample only.
      var fontCardsHtml =
        '<div class="baw-nameplate-fonts" role="group" aria-label="First line font">' +
        NAMEPLATE_FONTS.map(function (opt) {
          var selected = opt.id === font
          return (
            '<button type="button" class="baw-nameplate-font' +
            (selected ? ' is-selected' : '') + '"' +
            ' data-nameplate-font="' + opt.id + '" aria-pressed="' + (selected ? 'true' : 'false') + '"' +
            ' aria-label="' + escapeHtml(opt.label) + '">' +
            '<span class="baw-nameplate-font__check" aria-hidden="true"></span>' +
            '<span class="baw-nameplate-font__body">' +
            '<span class="baw-nameplate-font__sample baw-font-' + opt.id +
            '" aria-hidden="true" style="font-family: ' + opt.cssFamily + ';">' +
            escapeHtml(fontSample) + '</span>' +
            '<span class="baw-nameplate-font__label">' + escapeHtml(opt.label) + '</span>' +
            '</span></button>'
          )
        }).join('') +
        '</div>'

      var secondLineHtml = ''
      if (lines === 2) {
        var selectValue = secondMode === 'custom'
          ? NAMEPLATE_SECOND_LINE_CUSTOM_VALUE
          : (secondMode === 'suggestion' && line2 ? line2 : '')
        // Placeholder only — One line is “no second line”; Two lines requires L2.
        var line2Error = !!nameplate.missingLine2Error && !line2
        var line2ErrorId = 'baw-nameplate-line2-error'
        var optionsHtml =
          '<option value=""' + (selectValue === '' ? ' selected' : '') +
          '>Choose a second line\\u2026</option>' +
          suggestions.map(function (suggestion) {
            return (
              '<option value="' + escapeHtml(suggestion) + '"' +
              (selectValue === suggestion ? ' selected' : '') + '>' +
              escapeHtml(suggestion) +
              '</option>'
            )
          }).join('') +
          '<option value="' + NAMEPLATE_SECOND_LINE_CUSTOM_VALUE + '"' +
          (selectValue === NAMEPLATE_SECOND_LINE_CUSTOM_VALUE ? ' selected' : '') +
          '>Write your own</option>'

        secondLineHtml =
          '<div class="baw-nameplate-second-line">' +
          '<label class="baw-label" for="baw-nameplate-line2-select">Second line</label>' +
          '<select class="baw-select" id="baw-nameplate-line2-select" data-nameplate-line2-select' +
          (line2Error
            ? ' aria-invalid="true" aria-describedby="' + line2ErrorId + '"'
            : '') +
          '>' +
          optionsHtml +
          '</select>' +
          (secondMode === 'custom'
            ? '<input type="text" class="baw-input" id="baw-nameplate-text-2" data-nameplate-line2' +
              ' maxlength="' + NAMEPLATE_LINE2_MAX + '"' +
              ' autocomplete="off" spellcheck="false"' +
              ' aria-label="Custom second line" placeholder="e.g. Forever loved" value="' +
              escapeHtml(line2) + '"' +
              ' aria-describedby="' +
              (line2Error ? line2ErrorId + ' ' : '') +
              'baw-nameplate-line2-count"' +
              (line2Error
                ? ' aria-invalid="true"'
                : '') +
              ' />' +
              renderNameplateCharCounter(line2.length, NAMEPLATE_LINE2_MAX, 'baw-nameplate-line2-count')
            : '') +
          (line2Error
            ? '<p class="baw-error" id="' + line2ErrorId + '" data-nameplate-line2-error role="alert">' +
              'Please choose a second line.' +
              '</p>'
            : '') +
          '</div>'
      }

      return (
        '<div class="baw-field baw-field--nameplate-text">' +
        '<div class="baw-nameplate-first-line">' +
        '<span class="baw-label">First line</span>' +
        fontCardsHtml +
        '<input type="text" class="baw-input" id="baw-nameplate-text" data-nameplate-line1' +
        ' maxlength="' + NAMEPLATE_LINE1_MAX + '"' +
        ' autocomplete="off" spellcheck="false"' +
        ' aria-label="First line" placeholder="e.g. Bella" value="' + escapeHtml(line1) + '"' +
        ' aria-describedby="baw-nameplate-line1-count" />' +
        renderNameplateCharCounter(line1.length, NAMEPLATE_LINE1_MAX, 'baw-nameplate-line1-count') +
        '</div>' +
        secondLineHtml +
        '</div>'
      )
    }

    function renderNameplatePreviewHtml(nameplate, previewImage, opts) {
      opts = opts || {}
      var isSample = !!opts.sample
      var lines = normalizeNameplateLines(nameplate && nameplate.lines)
      var font = normalizeNameplateFont(nameplate.font)
      var fontDef = nameplateFontDef(font)
      var line1 = (nameplate.text || '').trim()
      var line2 = (nameplate.textLine2 || '').trim()
      // Uncommitted first screen: show a marketing L2 on the plate only (not in state).
      var sampleLine2 = (!line2 && isSample && lines === 2)
        ? nameplateSampleLine2DisplayText()
        : ''
      var line2Ink = line2 || sampleLine2
      var isScript = fontDef.id === 'lavanderia'
      var line1Style =
        'font-family: ' + fontDef.cssFamily + ';' +
        (isScript
          ? "font-feature-settings: 'liga' 1, 'kern' 1, 'calt' 0, 'dlig' 0, 'swsh' 0, 'titl' 0; font-variant-ligatures: common-ligatures; font-kerning: normal; word-spacing: 0.04em;"
          : 'letter-spacing: 0;')
      var debugAttr = nameplateSafeAreaDebugEnabled() ? ' data-nameplate-debug="1"' : ''

      // Preview always follows the Lines control. Empty committed L2 uses a
      // solid-ink “Second line” placeholder for geometry only — never cart/print.
      var lineMode = lines === 2 ? 'double' : 'single'
      var line2Display = line2Ink || (lines === 2 ? 'Second line' : '')
      var caption = isSample
        ? (opts.included
          ? 'Sample preview \\u2014 choose a colour to personalise the nameplate.'
          : 'Sample preview \\u2014 not on your order. Choose a colour to add a nameplate.')
        : 'Preview only \\u2014 final print placement may vary slightly.'
      var plateHtml =
        '<div class="baw-nameplate-preview__plate' + (previewImage ? '' : ' baw-nameplate-preview__plate--plain') +
        '" data-nameplate-preview-plate>' +
        (previewImage
          ? '<img class="baw-nameplate-preview__img" src="' + escapeHtml(nameplateArtboardUrl(previewImage)) + '" alt="" width="640" height="190" loading="eager" fetchpriority="high" decoding="async" />'
          : '') +
        // Physical SVG overlay: viewBox mm units — same system as production TIFF
        // (\`lib/nameplate-fit.ts\` / print/templates/nameplate/render.ts).
        '<svg class="baw-nameplate-preview__svg" viewBox="0 0 64 19" data-nameplate-svg data-nameplate-preview' +
        ' data-nameplate-font-mode="' + (isScript ? 'script' : 'print') + '"' +
        ' data-nameplate-line-mode="' + lineMode + '"' +
        ' data-nameplate-lines-ui="' + lines + '"' +
        debugAttr +
        ' preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        '<text class="baw-nameplate-preview__glyph baw-font-' + fontDef.id + (line1 ? '' : ' is-empty') + '"' +
        ' data-nameplate-line1 data-nameplate-svg-line1 text-anchor="middle" x="32" y="9.5" font-size="7" fill="#11110f" style="' + line1Style + '">' +
        escapeHtml(line1 || 'Your inscription') +
        '</text>' +
        (lines === 2
          ? '<text class="baw-nameplate-preview__glyph baw-nameplate-preview__glyph--body' +
            (line2Ink ? '' : ' is-empty') + '" data-nameplate-line2 data-nameplate-svg-line2 text-anchor="middle" x="32" y="12.7" font-size="3" fill="#11110f"' +
            ' style="font-family: \\'Source Serif Pro\\', Georgia, serif; letter-spacing: 0.01em;">' +
            escapeHtml(line2Display) +
            '</text>'
          : '') +
        '</svg></div>'
      // Review / modal thumbs: same fitted plate, no dock caption.
      if (opts.embed === 'plate') return plateHtml
      return (
        '<div class="baw-nameplate-preview' + (isSample ? ' baw-nameplate-preview--sample' : '') +
        '" aria-live="polite">' +
        plateHtml +
        '<p class="baw-field-hint baw-field-hint--quiet baw-nameplate-preview__caption">' +
        escapeHtml(caption) +
        '</p>' +
        '</div>'
      )
    }

    /**
     * Physical mm fit — keep in lockstep with \`lib/nameplate-fit.ts\`
     * (NAMEPLATE_FIT_SPEC / SAFE / CENTER / OPTICAL) and print/templates/nameplate/render.ts.
     * Preview differs only for empty line 2 (solid-ink placeholder; print omits it).
     */
    // Keep in lockstep with NAMEPLATE_FIT_SPEC in lib/nameplate-fit.ts.
    // Targets are visible getBBox ink heights (mm), not nominal font-size / pt.
    var NAMEPLATE_FIT_MM = {
      probe: 10,
      ink: '#11110f',
      center: { xMm: 32, yMm: 9.5 },
      safe: { widthMm: 58, heightMm: 14, minXMm: 3, maxXMm: 61, minYMm: 2.5, maxYMm: 16.5 },
      /** Wizard-only; never written to cart / TIFF. */
      line2Placeholder: 'Second line',
      opticalNudgeMm: {
        // Single-line: negative = raise text / more bottom pad (lockstep with lib/nameplate-fit.ts).
        single: { print: -0.29, script: -0.23 },
        // LOCKED / approved — two-line optical nudges (preview + TIFF sync with nameplate-fit.ts).
        double: { print: 0.36, script: 0.12 },
      },
      single: {
        // Short names hit full target; shrink only when measured width > max.
        print: { targetHeightMm: 8.5, maxHeightMm: 8.9, maxWidthMm: 58, minHeightMm: 4.0 },
        script: { targetHeightMm: 10.1, maxHeightMm: 10.5, maxWidthMm: 58, minHeightMm: 4.8 },
      },
      // LOCKED / approved (two-line typography — final). Print L1 8.1 / Script L1 9.4;
      // gaps Print 0.8 vs Script 0.55; L2 Print 3.9 / Script 3.7; optical print 0.36 / script 0.12.
      // Print L1 re-approved at 8.1 (was 7.8, previously 7.5); Print L2 at 3.9 (was 3.7);
      // Print gap at 0.8 (was 1.0, previously 1.25); Print optical at 0.36 (was 0.20, previously 0.08).
      // Script L1 re-approved at 9.4 (was 9.0); Script gap at 0.55 mm (was 0.7, previously 0.85 / 1.0).
      // Keep in sync with NAMEPLATE_FIT_SPEC.double + OPTICAL.double in lib/nameplate-fit.ts (preview + TIFF).
      double: {
        print: {
          // LOCKED / approved — Print L1 (was 7.8, previously 7.5).
          line1: { targetHeightMm: 8.1, maxWidthMm: 58, minHeightMm: 3.6, startCenterYMm: 7.35 },
          // LOCKED / approved — Print L2 body (was 3.7).
          line2: { targetHeightMm: 3.9, maxWidthMm: 57, minHeightMm: 3.53, startCenterYMm: 13.15 },
          // LOCKED / approved — Print gap (was 1.0, previously 1.25).
          gapMm: 0.8,
        },
        script: {
          // LOCKED / approved — Script L1 (was 9.0).
          line1: { targetHeightMm: 9.4, maxWidthMm: 58, minHeightMm: 4.2, startCenterYMm: 7.1 },
          // LOCKED / approved — Script L2 body (unchanged; Print L2 is 3.9).
          line2: { targetHeightMm: 3.7, maxWidthMm: 57, minHeightMm: 3.53, startCenterYMm: 13.55 },
          // LOCKED / approved — Script gap 0.55 (was 0.7, previously 0.85 / 1.0; tighter than Print 0.8 → more bottom air).
          gapMm: 0.55,
        },
      },
    }

    function nameplateSafeAreaDebugEnabled() {
      try {
        var params = new URLSearchParams(window.location.search || '')
        var q = params.get('nameplateDebug') || params.get('npSafeArea') || params.get('nameplateSafeArea')
        return q === '1' || q === 'true'
      } catch (err) {
        return false
      }
    }

    function scaleNameplateBox(box, fromSize, toSize) {
      var s = toSize / fromSize
      return { x: box.x * s, y: box.y * s, width: box.width * s, height: box.height * s }
    }

    /** Off-screen SVG getBBox in mm viewBox units (visible ink, not CSS line box). */

    function measureNameplateGlyphMm(text, fontFamily, letterSpacing, extraStyle, fontSizeMm, fontWeight) {
      if (!text) return { x: 0, y: 0, width: 0, height: 0 }
      var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
      svg.setAttribute('viewBox', '0 0 64 19')
      svg.setAttribute('width', '640')
      svg.setAttribute('height', '190')
      svg.style.cssText = 'position:absolute;left:-10000px;top:0;width:640px;height:190px;visibility:hidden;pointer-events:none;'
      var t = document.createElementNS('http://www.w3.org/2000/svg', 'text')
      t.setAttribute('x', '0')
      t.setAttribute('y', '0')
      t.setAttribute('text-anchor', 'middle')
      t.setAttribute('font-size', String(fontSizeMm))
      t.setAttribute('font-family', fontFamily)
      // Match preview glyph CSS (500) and @font-face (400) — browsers resolve to the Regular face.
      t.setAttribute('font-weight', fontWeight || '500')
      t.setAttribute('letter-spacing', letterSpacing || '0')
      t.setAttribute('fill', NAMEPLATE_FIT_MM.ink)
      if (extraStyle) t.setAttribute('style', extraStyle)
      t.textContent = text
      svg.appendChild(t)
      document.body.appendChild(svg)
      var box = { x: 0, y: 0, width: 0, height: 0 }
      try {
        var b = t.getBBox()
        box = { x: b.x, y: b.y, width: b.width, height: b.height }
      } catch (err) {}
      document.body.removeChild(svg)
      // Zero bbox while fonts settle — approximate so target-height fit still runs.
      if (!(box.height > 0) && text) {
        var approxH = fontSizeMm * 0.72
        var approxW = Math.max(fontSizeMm * 0.45 * String(text).length, fontSizeMm * 0.35)
        box = { x: -approxW / 2, y: -approxH * 0.78, width: approxW, height: approxH }
      }
      return box
    }

    /** Scale probe getBBox to target ink height; shrink uniformly if wider than maxW. */

    function fitNameplateLineMm(metrics, targetH, maxH, maxW, minH, centerY) {
      var CENTER = NAMEPLATE_FIT_MM.center
      var PROBE = NAMEPLATE_FIT_MM.probe
      if (!(metrics.height > 0)) {
        return {
          fontSizeMm: minH,
          xMm: CENTER.xMm,
          yMm: centerY,
          centerYMm: centerY,
          inkWidthMm: 0,
          inkHeightMm: 0,
          tooLong: false,
        }
      }
      var fontSizeMm = PROBE * (targetH / metrics.height)
      if (maxH != null && fontSizeMm > PROBE * (maxH / metrics.height)) {
        fontSizeMm = PROBE * (maxH / metrics.height)
      }
      var box = scaleNameplateBox(metrics, PROBE, fontSizeMm)
      if (box.width > maxW && box.width > 0) {
        fontSizeMm *= maxW / box.width
        box = scaleNameplateBox(metrics, PROBE, fontSizeMm)
      }
      var tooLong = box.height + 1e-6 < minH
      var inkCenterX = box.x + box.width / 2
      var inkCenterY = box.y + box.height / 2
      return {
        fontSizeMm: fontSizeMm,
        xMm: CENTER.xMm - inkCenterX,
        yMm: centerY - inkCenterY,
        centerYMm: centerY,
        inkWidthMm: box.width,
        inkHeightMm: box.height,
        tooLong: tooLong,
      }
    }

    function applyNameplateSvgLine(el, line) {
      if (!el || !line) return
      el.setAttribute('x', line.xMm.toFixed(4))
      el.setAttribute('y', line.yMm.toFixed(4))
      // Presentation attribute in viewBox user units (1 = 1 mm). Clear any CSS
      // font-size so inherited rem/px from \`.baw\` cannot override the mm size.
      el.setAttribute('font-size', line.fontSizeMm.toFixed(4))
      if (el.style) {
        el.style.removeProperty('font-size')
        el.style.removeProperty('opacity')
        el.style.opacity = '1'
      }
      el.setAttribute('text-anchor', 'middle')
      el.setAttribute('fill', NAMEPLATE_FIT_MM.ink)
      el.setAttribute('opacity', '1')
      el.removeAttribute('fill-opacity')
    }

    function nameplatePreviewRealTexts(scope, t1, t2) {
      var input1 = scope.querySelector('input[data-nameplate-line1]')
      var input2 = scope.querySelector('input[data-nameplate-line2]')
      var select2 = scope.querySelector('select[data-nameplate-line2-select]')
      var text1 = input1
        ? String(input1.value || '').trim()
        : (t1 && !t1.classList.contains('is-empty') ? String(t1.textContent || '').trim() : '')
      var text2 = ''
      if (select2) {
        var selVal = String(select2.value || '')
        if (selVal === NAMEPLATE_SECOND_LINE_CUSTOM_VALUE) {
          text2 = input2 ? String(input2.value || '').trim() : ''
        } else {
          text2 = selVal
        }
      } else if (input2) {
        text2 = String(input2.value || '').trim()
      } else if (t2 && !t2.classList.contains('is-empty')) {
        text2 = String(t2.textContent || '').trim()
      }
      // Empty line 1 still shows a ghost label for the vacant plate — measure that
      // for a sensible preview size.
      if (!text1 && t1) text1 = String(t1.textContent || '').trim() || 'Your inscription'
      return { text1: text1, text2: text2 }
    }

    function logNameplateFitDebug(payload) {
      if (!nameplateSafeAreaDebugEnabled()) return
      try {
        console.info('[nameplateFit]', payload)
      } catch (err) {}
    }

    function ensureNameplateSafeGuides(svg) {
      var existing = svg.querySelector('[data-nameplate-safe-guide]')
      var show = svg.getAttribute('data-nameplate-debug') === '1' || nameplateSafeAreaDebugEnabled()
      if (!show) {
        if (existing && existing.parentNode) existing.parentNode.removeChild(existing)
        return
      }
      if (existing) return
      var SAFE = NAMEPLATE_FIT_MM.safe
      var g = document.createElementNS('http://www.w3.org/2000/svg', 'g')
      g.setAttribute('data-nameplate-safe-guide', '1')
      g.setAttribute('pointer-events', 'none')
      var plate = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
      plate.setAttribute('x', '0')
      plate.setAttribute('y', '0')
      plate.setAttribute('width', '64')
      plate.setAttribute('height', '19')
      plate.setAttribute('fill', 'none')
      plate.setAttribute('stroke', 'rgba(0,120,255,0.55)')
      plate.setAttribute('stroke-width', '0.15')
      var safeR = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
      safeR.setAttribute('x', String(SAFE.minXMm))
      safeR.setAttribute('y', String(SAFE.minYMm))
      safeR.setAttribute('width', String(SAFE.widthMm))
      safeR.setAttribute('height', String(SAFE.heightMm))
      safeR.setAttribute('fill', 'none')
      safeR.setAttribute('stroke', 'rgba(0,180,80,0.7)')
      safeR.setAttribute('stroke-width', '0.12')
      safeR.setAttribute('stroke-dasharray', '0.6 0.4')
      g.appendChild(plate)
      g.appendChild(safeR)
      svg.insertBefore(g, svg.firstChild)
    }

    /**
     * Physical-coordinate fit on the 64×19 mm SVG overlay. Independent of
     * viewport width — the SVG scales uniformly with the plate image.
     * Keep in lockstep with \`lib/nameplate-fit.ts\` / print client script.
     * Preview: Two lines UI ⇒ always double geometry (placeholder if L2 empty).
     * Print/TIFF: double only when L2 has real copy.
     */

    function fitNameplatePreview(scopeRoot) {
      // rAF may pass a timestamp — only treat real Elements as an alternate scope.
      var scope =
        scopeRoot && typeof scopeRoot.querySelector === 'function' ? scopeRoot : els.step
      if (!scope) return
      var svg = scope.querySelector('[data-nameplate-svg]')
      if (!svg) return

      ensureNameplateSafeGuides(svg)

      var fontMode = svg.getAttribute('data-nameplate-font-mode') === 'script' ? 'script' : 'print'
      var t1 = svg.querySelector('[data-nameplate-line1], [data-nameplate-svg-line1]')
      var t2 = svg.querySelector('[data-nameplate-line2], [data-nameplate-svg-line2]')
      var real = nameplatePreviewRealTexts(scope, t1, t2)
      var text1 = real.text1
      var text2 = real.text2
      // Follow the Lines control, not whether line 2 has copy yet.
      var linesUiAttr = Number(svg.getAttribute('data-nameplate-lines-ui'))
      var linesUi = linesUiAttr === 2 ? 2 : linesUiAttr === 1 ? 1 : (t2 ? 2 : 1)
      var lineMode = linesUi === 2 && t2 ? 'double' : 'single'
      svg.setAttribute('data-nameplate-line-mode', lineMode)
      var line2IsPlaceholder = lineMode === 'double' && !text2
      var measureText2 = text2 || (line2IsPlaceholder ? NAMEPLATE_FIT_MM.line2Placeholder : '')

      var input1 = scope.querySelector('input[data-nameplate-line1]')
      if (t1 && input1) {
        var raw1 = String(input1.value || '').trim()
        t1.textContent = raw1 || 'Your inscription'
        if (raw1) t1.classList.remove('is-empty')
        else t1.classList.add('is-empty')
      }
      if (t2) {
        t2.textContent = measureText2
        t2.setAttribute('visibility', 'visible')
        if (text2) t2.classList.remove('is-empty')
        else t2.classList.add('is-empty')
      }

      var family1 = (t1 && t1.style && t1.style.fontFamily) || (fontMode === 'script'
        ? "'Lavanderia', cursive"
        : "'Alice', Georgia, serif")
      var tracking1 = '0'
      var extra1 = fontMode === 'script'
        ? "font-feature-settings: 'liga' 1, 'kern' 1, 'calt' 0, 'dlig' 0, 'swsh' 0, 'titl' 0; font-variant-ligatures: common-ligatures; font-kerning: normal; word-spacing: 0.04em;"
        : ''
      var PROBE = NAMEPLATE_FIT_MM.probe
      var CENTER = NAMEPLATE_FIT_MM.center
      var SAFE = NAMEPLATE_FIT_MM.safe
      var opticalNudge = (NAMEPLATE_FIT_MM.opticalNudgeMm[lineMode] || NAMEPLATE_FIT_MM.opticalNudgeMm.single)[fontMode] || 0
      var opticalY = CENTER.yMm + opticalNudge
      var m1 = measureNameplateGlyphMm(text1, family1, tracking1, extra1, PROBE, '500')
      var tooLong = false

      if (lineMode === 'single') {
        var single = NAMEPLATE_FIT_MM.single[fontMode]
        var one = fitNameplateLineMm(
          m1,
          single.targetHeightMm,
          single.maxHeightMm,
          single.maxWidthMm,
          single.minHeightMm,
          opticalY
        )
        var halfW = one.inkWidthMm / 2
        var top = one.centerYMm - one.inkHeightMm / 2
        var bottom = one.centerYMm + one.inkHeightMm / 2
        var overflowsY = top < SAFE.minYMm || bottom > SAFE.maxYMm
        var overflowsX = halfW > SAFE.widthMm / 2
        if ((overflowsY || overflowsX) && one.inkHeightMm > 0) {
          var hs = one.inkHeightMm > SAFE.heightMm ? SAFE.heightMm / one.inkHeightMm : 1
          var maxW = Math.min(single.maxWidthMm, SAFE.widthMm)
          var ws = one.inkWidthMm > maxW ? maxW / one.inkWidthMm : 1
          var sc = Math.min(hs, ws)
          function placeSingle(nextSize) {
            var box = scaleNameplateBox(m1, PROBE, nextSize)
            var icx = box.x + box.width / 2
            var icy = box.y + box.height / 2
            var halfH = box.height / 2
            var placeY = opticalY
            if (placeY - halfH < SAFE.minYMm) placeY = SAFE.minYMm + halfH
            if (placeY + halfH > SAFE.maxYMm) placeY = SAFE.maxYMm - halfH
            return {
              fontSizeMm: nextSize,
              xMm: CENTER.xMm - icx,
              yMm: placeY - icy,
              centerYMm: placeY,
              inkWidthMm: box.width,
              inkHeightMm: box.height,
              tooLong: box.height + 1e-6 < single.minHeightMm,
            }
          }
          if (sc < 0.999) one = placeSingle(one.fontSizeMm * sc)
          else if (overflowsY) one = placeSingle(one.fontSizeMm)
        }
        tooLong = !!one.tooLong
        applyNameplateSvgLine(t1, one)
        if (t2) {
          t2.setAttribute('visibility', 'hidden')
          t2.textContent = ''
        }
        logNameplateFitDebug({
          fontMode: fontMode,
          lineMode: 'single',
          opticalNudgeMm: opticalNudge,
          targets: single,
          line1: {
            text: text1,
            fontSizeMm: one.fontSizeMm,
            inkHeightMm: one.inkHeightMm,
            inkWidthMm: one.inkWidthMm,
            centerYMm: one.centerYMm,
          },
          line2: null,
          tooLong: tooLong,
        })
      } else {
        var dbl = NAMEPLATE_FIT_MM.double[fontMode]
        var m2 = measureNameplateGlyphMm(
          measureText2,
          "'Source Serif Pro', Georgia, serif",
          '0.01em',
          '',
          PROBE,
          '500'
        )
        var a = fitNameplateLineMm(
          m1,
          dbl.line1.targetHeightMm,
          null,
          dbl.line1.maxWidthMm,
          dbl.line1.minHeightMm,
          dbl.line1.startCenterYMm
        )
        var b = fitNameplateLineMm(
          m2,
          dbl.line2.targetHeightMm,
          null,
          dbl.line2.maxWidthMm,
          dbl.line2.minHeightMm,
          dbl.line2.startCenterYMm
        )
        var size1 = a.fontSizeMm
        var size2 = b.fontSizeMm
        // Placeholder L2 must not trip the "too long" warning.
        tooLong = !!(a.tooLong || (!line2IsPlaceholder && b.tooLong))

        function placePair(s1, s2) {
          var b1 = scaleNameplateBox(m1, PROBE, s1)
          var b2 = scaleNameplateBox(m2, PROBE, s2)
          var gap = dbl.gapMm
          var combined = b1.height + gap + b2.height
          var topY = opticalY - combined / 2
          var c1 = topY + b1.height / 2
          var c2 = topY + b1.height + gap + b2.height / 2
          return {
            line1: {
              fontSizeMm: s1,
              xMm: CENTER.xMm - (b1.x + b1.width / 2),
              yMm: c1 - (b1.y + b1.height / 2),
              centerYMm: c1,
              inkWidthMm: b1.width,
              inkHeightMm: b1.height,
            },
            line2: {
              fontSizeMm: s2,
              xMm: CENTER.xMm - (b2.x + b2.width / 2),
              yMm: c2 - (b2.y + b2.height / 2),
              centerYMm: c2,
              inkWidthMm: b2.width,
              inkHeightMm: b2.height,
            },
            combinedHeight: combined,
            top: topY,
            bottom: topY + combined,
          }
        }

        var placed = placePair(size1, size2)
        if (
          placed.combinedHeight <= SAFE.heightMm + 1e-6 &&
          (placed.top < SAFE.minYMm || placed.bottom > SAFE.maxYMm)
        ) {
          var shift = 0
          if (placed.top < SAFE.minYMm) shift = SAFE.minYMm - placed.top
          if (placed.bottom + shift > SAFE.maxYMm) shift = SAFE.maxYMm - placed.bottom
          if (Math.abs(shift) > 1e-6) {
            placed.line1.yMm += shift
            placed.line1.centerYMm += shift
            placed.line2.yMm += shift
            placed.line2.centerYMm += shift
            placed.top += shift
            placed.bottom += shift
          }
        }
        for (var pass = 0; pass < 4; pass++) {
          var overTop = placed.top < SAFE.minYMm
          var overBottom = placed.bottom > SAFE.maxYMm
          var wide1 = placed.line1.inkWidthMm > dbl.line1.maxWidthMm
          var wide2 = placed.line2.inkWidthMm > dbl.line2.maxWidthMm
          if (!overTop && !overBottom && !wide1 && !wide2) break
          var scale = 1
          if ((overTop || overBottom) && placed.combinedHeight > SAFE.heightMm && placed.combinedHeight > 0) {
            scale = Math.min(scale, SAFE.heightMm / placed.combinedHeight)
          }
          if (wide1 && placed.line1.inkWidthMm > 0) {
            scale = Math.min(scale, dbl.line1.maxWidthMm / placed.line1.inkWidthMm)
          }
          if (wide2 && placed.line2.inkWidthMm > 0) {
            scale = Math.min(scale, dbl.line2.maxWidthMm / placed.line2.inkWidthMm)
          }
          if (scale >= 0.999) break
          size1 *= scale
          size2 *= scale
          placed = placePair(size1, size2)
        }
        if (
          placed.line1.inkHeightMm + 1e-6 < dbl.line1.minHeightMm ||
          (!line2IsPlaceholder && placed.line2.inkHeightMm + 1e-6 < dbl.line2.minHeightMm)
        ) {
          tooLong = true
        }
        applyNameplateSvgLine(t1, placed.line1)
        applyNameplateSvgLine(t2, placed.line2)
        logNameplateFitDebug({
          fontMode: fontMode,
          lineMode: 'double',
          opticalNudgeMm: opticalNudge,
          line2Placeholder: line2IsPlaceholder,
          targets: dbl,
          gapMm: dbl.gapMm,
          line1: {
            text: text1,
            fontSizeMm: placed.line1.fontSizeMm,
            inkHeightMm: placed.line1.inkHeightMm,
            inkWidthMm: placed.line1.inkWidthMm,
            centerYMm: placed.line1.centerYMm,
          },
          line2: {
            text: measureText2,
            fontSizeMm: placed.line2.fontSizeMm,
            inkHeightMm: placed.line2.inkHeightMm,
            inkWidthMm: placed.line2.inkWidthMm,
            centerYMm: placed.line2.centerYMm,
          },
          tooLong: tooLong,
        })
      }

      svg.setAttribute('data-nameplate-too-long', tooLong ? '1' : '0')
    }

    /** Force Alice / Lavanderia / Source Serif into the document before measuring
     *  so getBBox isn't stuck on Georgia/cursive fallback after fonts.ready. */

    function ensureNameplateFontsThenFit(scopeRoot) {
      var scope =
        scopeRoot && typeof scopeRoot.querySelector === 'function' ? scopeRoot : els.step
      var run = function () { fitNameplatePreview(scope) }
      if (!document.fonts || typeof document.fonts.load !== 'function') {
        run()
        if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
          document.fonts.ready.then(run)
        }
        return
      }
      var loads = [
        document.fonts.load("400 48px 'Alice'"),
        document.fonts.load("500 48px 'Alice'"),
        document.fonts.load("400 48px 'Lavanderia'"),
        document.fonts.load("500 48px 'Lavanderia'"),
        document.fonts.load("400 48px 'Source Serif Pro'"),
        document.fonts.load("500 48px 'Source Serif Pro'"),
      ]
      Promise.all(loads.map(function (p) {
        return p.catch(function () { return null })
      })).then(run)
    }

    /** Layout and webfonts both settle after the markup lands: fit on the next
     *  frame, then again once the inscription faces have actually loaded. */

    function scheduleNameplateFit() {
      if (!els.step || !els.step.querySelector('[data-nameplate-preview]')) return
      window.requestAnimationFrame(function () {
        fitNameplatePreview()
        ensureNameplateFontsThenFit()
      })
    }

    /** Text inputs update the preview in place; layout/font changes re-render the step. */

    function wireNameplatePersonalise(entry, groupKey, productId) {
      // Capture the focused product id — never close over an outer \`pid\` (strict
      // mode ReferenceError left Print/Script + One/Two re-renders dead after the
      // first state write).
      var pid = productId != null && String(productId) !== ''
        ? String(productId)
        : resolveMemorialAddonProductId(state, config, groupKey, 'nameplate', null)

      // Always resolve the canonical itemAddons bucket — never close over a stale
      // entry.addons reference (draft serialize / ledger focus can retarget it).
      function liveNameplate() {
        return canonicalNameplateAddon(groupKey, pid)
      }

      var line1 = els.step.querySelector('input[data-nameplate-line1]')
      if (line1) {
        line1.addEventListener('input', function () {
          var nameplate = liveNameplate()
          var cleaned = sanitizeNameplateLine(line1.value, NAMEPLATE_LINE1_MAX)
          if (cleaned !== line1.value) {
            var start = line1.selectionStart
            line1.value = cleaned
            try {
              var pos = Math.min(cleaned.length, typeof start === 'number' ? start : cleaned.length)
              line1.setSelectionRange(pos, pos)
            } catch (err) { /* ignore */ }
          }
          var value = cleaned.trim()
          nameplate.text = value || null
          var counter1 = els.step.querySelector('#baw-nameplate-line1-count')
          if (counter1) counter1.textContent = String(line1.value || '').length + ' / ' + NAMEPLATE_LINE1_MAX
          updateNameplatePreviewLine('[data-nameplate-svg-line1], [data-nameplate-line1]', value, 'Your inscription')
          fitNameplatePreview()
          renderSummary()
          scheduleDraftSave()
          setContinueEnabled(canAdvanceFrom(state.currentStepId))
        })
      }

      function clearNameplateLine2ErrorUi(nameplate) {
        if (!nameplate.missingLine2Error) return
        nameplate.missingLine2Error = false
        var selectEl = els.step.querySelector('select[data-nameplate-line2-select]')
        var customEl = els.step.querySelector('input[data-nameplate-line2]')
        ;[selectEl, customEl].forEach(function (el) {
          if (!el) return
          el.removeAttribute('aria-invalid')
          el.removeAttribute('aria-describedby')
        })
        var errEl = els.step.querySelector('[data-nameplate-line2-error]')
        if (errEl && errEl.parentNode) errEl.parentNode.removeChild(errEl)
      }

      function applyNameplateLine2Preview(value) {
        // Empty L2 keeps solid-ink preview placeholder so two-line geometry stays active.
        // Target SVG glyphs only — never the custom <input data-nameplate-line2>.
        updateNameplatePreviewLine(
          '[data-nameplate-svg-line2]',
          value,
          NAMEPLATE_FIT_MM.line2Placeholder
        )
        fitNameplatePreview()
        renderSummary()
        scheduleDraftSave()
        // Live Continue: suggestion / custom L2 without a full step re-render.
        setContinueEnabled(canAdvanceFrom(state.currentStepId))
      }

      var line2Select = els.step.querySelector('select[data-nameplate-line2-select]')
      if (line2Select) {
        line2Select.addEventListener('change', function () {
          var nameplate = liveNameplate()
          var previousMode = nameplate.secondLineMode
          var value = String(line2Select.value || '')
          // Any Second line control change counts as an explicit L2 choice.
          nameplate._line2Touched = true
          if (value === NAMEPLATE_SECOND_LINE_CUSTOM_VALUE) {
            nameplate.secondLineMode = 'custom'
            // Fresh custom field — don't carry a suggestion string into the input.
            nameplate.textLine2 = null
            nameplate.missingLine2Error = false
            renderMemorialAddonNameplateStep(groupKey, pid)
            renderSummary()
            scheduleDraftSave()
            focusNameplateSecondLine()
            return
          }
          if (value) {
            nameplate.secondLineMode = 'suggestion'
            nameplate.textLine2 = value
            nameplate.missingLine2Error = false
            // Leaving custom hides the free-text input; suggestion → suggestion can
            // update the preview in place without a full step re-render.
            if (previousMode === 'custom') {
              renderMemorialAddonNameplateStep(groupKey, pid)
              renderSummary()
              scheduleDraftSave()
              return
            }
            clearNameplateLine2ErrorUi(nameplate)
            applyNameplateLine2Preview(value)
            return
          }
          nameplate.secondLineMode = null
          nameplate.textLine2 = null
          // Back to “Choose a second line…” — stay in Two lines; One line is the toggle.
          nameplate._line2Touched = false
          nameplate.missingLine2Error = false
          if (previousMode === 'custom') {
            renderMemorialAddonNameplateStep(groupKey, pid)
            renderSummary()
            scheduleDraftSave()
            return
          }
          applyNameplateLine2Preview('')
        })
      }

      var line2 = els.step.querySelector('input[data-nameplate-line2]')
      if (line2) {
        line2.addEventListener('input', function () {
          var nameplate = liveNameplate()
          var cleaned = sanitizeNameplateLine(line2.value, NAMEPLATE_LINE2_MAX)
          if (cleaned !== line2.value) {
            var start2 = line2.selectionStart
            line2.value = cleaned
            try {
              var pos2 = Math.min(cleaned.length, typeof start2 === 'number' ? start2 : cleaned.length)
              line2.setSelectionRange(pos2, pos2)
            } catch (err) { /* ignore */ }
          }
          var value = cleaned.trim()
          nameplate._line2Touched = true
          nameplate.secondLineMode = 'custom'
          nameplate.textLine2 = value || null
          var counter2 = els.step.querySelector('#baw-nameplate-line2-count')
          if (counter2) counter2.textContent = String(line2.value || '').length + ' / ' + NAMEPLATE_LINE2_MAX
          if (value) clearNameplateLine2ErrorUi(nameplate)
          applyNameplateLine2Preview(value)
        })
      }

      // Bind One/Two + Print/Script directly on the buttons (not delegated) so the
      // first tap always hits a live handler on the current DOM nodes.
      Array.prototype.forEach.call(
        els.step.querySelectorAll('button.baw-nameplate-layout__btn[data-nameplate-lines]'),
        function (btn) {
          btn.addEventListener('click', function (e) {
            e.preventDefault()
            var nameplate = liveNameplate()
            var nextLines = normalizeNameplateLines(btn.getAttribute('data-nameplate-lines'))
            // Pin before any re-render so ensureNameplateInscriptionDefaults cannot
            // force One line back over an explicit Two lines choice.
            nameplate._linesTouched = true
            var currentLines = normalizeNameplateLines(nameplate.lines)
            if (currentLines === nextLines && nameplate.lines === nextLines &&
                btn.classList.contains('is-selected')) {
              return
            }
            // Instant selected chrome so the first tap never feels ignored while
            // the step re-renders (1↔2 line form swap).
            Array.prototype.forEach.call(
              els.step.querySelectorAll('button.baw-nameplate-layout__btn[data-nameplate-lines]'),
              function (b) {
                var on = normalizeNameplateLines(b.getAttribute('data-nameplate-lines')) === nextLines
                b.classList.toggle('is-selected', on)
                b.setAttribute('aria-pressed', on ? 'true' : 'false')
              }
            )
            nameplate.lines = nextLines
            if (nextLines === 1) {
              nameplate.textLine2 = null
              nameplate.secondLineMode = null
              nameplate._line2Touched = false
              nameplate.missingLine2Error = false
            }
            // Two lines: leave L2 empty so the control shows “Choose a second line…”
            // (do not pre-fill a suggestion — same as colour-commit open).
            a11yRestoreFocus =
              'button.baw-nameplate-layout__btn[data-nameplate-lines="' + nextLines + '"]'
            renderMemorialAddonNameplateStep(groupKey, pid)
            renderSummary()
            scheduleDraftSave()
          })
        }
      )

      Array.prototype.forEach.call(
        els.step.querySelectorAll('button.baw-nameplate-font[data-nameplate-font]'),
        function (btn) {
          btn.addEventListener('click', function (e) {
            e.preventDefault()
            var nameplate = liveNameplate()
            var nextFont = normalizeNameplateFont(btn.getAttribute('data-nameplate-font'))
            // Pin before any re-render so defaults / draft restore cannot wipe an
            // explicit Print/Script choice.
            nameplate._fontTouched = true
            var currentFont = normalizeNameplateFont(nameplate.font)
            if (currentFont === nextFont && nameplate.font === nextFont &&
                btn.classList.contains('is-selected')) {
              return
            }
            // Instant selected chrome so the first tap never feels ignored while
            // the step re-renders (preview font swap).
            Array.prototype.forEach.call(
              els.step.querySelectorAll('button.baw-nameplate-font[data-nameplate-font]'),
              function (b) {
                var on = normalizeNameplateFont(b.getAttribute('data-nameplate-font')) === nextFont
                b.classList.toggle('is-selected', on)
                b.setAttribute('aria-pressed', on ? 'true' : 'false')
              }
            )
            nameplate.font = nextFont
            a11yRestoreFocus = 'button.baw-nameplate-font[data-nameplate-font="' + nextFont + '"]'
            renderMemorialAddonNameplateStep(groupKey, pid)
            renderSummary()
            scheduleDraftSave()
          })
        }
      )
    }

    function updateNameplatePreviewLine(selector, value, placeholder) {
      if (!els.step) return
      // Only touch SVG <text> glyphs — never inputs/selects that share data attrs.
      var nodes = els.step.querySelectorAll(selector)
      if (!nodes || !nodes.length) return
      var next = value || placeholder || ''
      Array.prototype.forEach.call(nodes, function (el) {
        if (!el || el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA') {
          return
        }
        el.textContent = next
        if (value) {
          el.classList.remove('is-empty')
          el.setAttribute('visibility', 'visible')
        } else {
          el.classList.add('is-empty')
          // Ghost placeholders (empty L1 / empty L2) stay visible for layout.
          if (!placeholder) el.setAttribute('visibility', 'hidden')
          else el.setAttribute('visibility', 'visible')
        }
      })
    }

    /** Whether the family opted into the engraving add-on (card selected). */`
  var INSTALL_EXPORTS = [
    "canonicalNameplateAddon",
    "isCurrentNameplateStep",
    "openNameplatePreviewModal",
    "summaryNameplatePreviewButton",
    "focusNameplateSecondLine",
    "syncNameplateSecondLineFromDom",
    "syncNameplateInscriptionFromDom",
    "tryAdvanceMemorialNameplateStep",
    "renderMemorialAddonNameplateStep",
    "layoutNameplateDock",
    "clearNameplateSelection",
    "commitIncludedNameplateSampleDefaults",
    "preloadNameplateTextFromPet",
    "nameplateSampleLine2DisplayText",
    "ensureNameplateInscriptionDefaults",
    "nameplateFontSampleText",
    "renderNameplateLayoutHtml",
    "renderNameplatePersonaliseHtml",
    "renderNameplatePreviewHtml",
    "nameplateSafeAreaDebugEnabled",
    "scaleNameplateBox",
    "measureNameplateGlyphMm",
    "fitNameplateLineMm",
    "applyNameplateSvgLine",
    "nameplatePreviewRealTexts",
    "logNameplateFitDebug",
    "ensureNameplateSafeGuides",
    "fitNameplatePreview",
    "ensureNameplateFontsThenFit",
    "scheduleNameplateFit",
    "wireNameplatePersonalise",
    "updateNameplatePreviewLine"
  ]

  function install(ctx) {
    var api = {}
    // Sloppy-mode runner: with (ctx) resolves free identifiers to the live
    // wizard context (state, els, render, helpers). Function declarations in
    // INSTALL_SRC become install-locals and are exported onto api.
    var body = 'with (ctx) {\n' + INSTALL_SRC + '\n'
    for (var i = 0; i < INSTALL_EXPORTS.length; i++) {
      var name = INSTALL_EXPORTS[i]
      body += 'api[' + JSON.stringify(name) + '] = ' + name + ';\n'
    }
    body += '}'
    var runner = new Function('ctx', 'api', body)
    runner(ctx, api)
    return api
  }

  global.AngelAshesBookingNameplate = {
    create: create,
    install: install,
  }
})(typeof window !== 'undefined' ? window : this)
